import * as React from "react";
import { useContext, useState, useEffect, useRef } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Container,
  Button
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { Link, useLocation, useNavigate } from "react-router-dom";
import UserContext from "./UserContext";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const ACCENT = "#3854e8";

const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";
const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

const practicePages = [
  { name: "Job position", link: "/job", hint: "Drill questions for a role" },
  {
    name: "Job posting",
    link: "/jobTailored",
    hint: "Pull from a real description"
  },
  {
    name: "Resume × posting",
    link: "/resumeQuestions",
    hint: "Cross-reference both"
  },
  {
    name: "Bring your own",
    link: "/import",
    hint: "Practice a custom list"
  }
];

const navLinkSx = (active = false) => ({
  fontFamily: SANS,
  fontSize: "0.92rem",
  fontWeight: 500,
  color: active ? INK : INK_SOFT,
  textTransform: "none",
  letterSpacing: "0",
  px: 1.5,
  py: 1,
  minWidth: 0,
  borderRadius: "10px",
  position: "relative",
  transition: "color 0.18s ease, background-color 0.18s ease",
  "&:hover": {
    color: INK,
    backgroundColor: "rgba(15, 17, 33, 0.04)"
  },
  "&::after": active
    ? {
        content: '""',
        position: "absolute",
        left: "50%",
        bottom: 4,
        transform: "translateX(-50%)",
        width: 14,
        height: 2,
        borderRadius: "999px",
        backgroundColor: ACCENT
      }
    : {}
});

const initialFromName = name => {
  if (!name) return "";
  const parts = String(name).trim().split(/\s+/);
  return (parts[0]?.[0] || "").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
};

function MenuBar() {
  const [practiceAnchor, setPracticeAnchor] = useState(null);
  const [mobileAnchor, setMobileAnchor] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => {
      setPracticeAnchor(null);
      setMobileAnchor(null);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const closeAll = () => {
    setPracticeAnchor(null);
    setMobileAnchor(null);
  };

  const goHowItWorks = () => {
    navigate("/", { state: { scrollTo: "how-it-works" } });
    closeAll();
  };
  const goPricing = () => {
    navigate("/", { state: { scrollTo: "pricing" } });
    closeAll();
  };

  const isActive = paths =>
    paths.some(p =>
      p === "/"
        ? location.pathname === "/"
        : location.pathname.startsWith(p)
    );

  const isPracticeActive = isActive([
    "/job",
    "/jobTailored",
    "/import",
    "/resumeQuestions"
  ]);
  const isProfileActive = isActive(["/profile"]);

  const userName = user?.attributes?.name || user?.username;
  const initials = initialFromName(userName);

  const wordmark = (
    <Box
      component={Link}
      to="/"
      sx={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: "5px",
        textDecoration: "none",
        fontFamily: SANS,
        fontWeight: 600,
        fontSize: "1.05rem",
        lineHeight: 1,
        letterSpacing: "-0.02em",
        color: INK,
        transition: "opacity 0.18s ease",
        "&:hover": { opacity: 0.85 }
      }}
    >
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: "2px",
          backgroundColor: ACCENT,
          display: "inline-block",
          alignSelf: "center",
          mr: "4px"
        }}
      />
      <span>interview lab</span>
      <Box
        component="span"
        sx={{
          fontWeight: 500,
          color: INK_FAINT,
          ml: "2px"
        }}
      >
        co
      </Box>
    </Box>
  );

  const menuPaperSx = {
    mt: 1,
    minWidth: 280,
    borderRadius: "16px",
    border: `1px solid ${INK_HAIRLINE}`,
    backgroundColor: "#ffffff",
    boxShadow:
      "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -32px rgba(15, 17, 33, 0.18), 0 4px 10px -6px rgba(15, 17, 33, 0.08)",
    p: 0.75,
    "& .MuiList-root": { py: 0 }
  };

  const renderPracticeItem = page => (
    <MenuItem
      key={page.link}
      component={Link}
      to={page.link}
      onClick={closeAll}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.25,
        py: 1.1,
        px: 1.5,
        borderRadius: "10px",
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: "rgba(15, 17, 33, 0.04)" }
      }}
    >
      <Typography
        sx={{
          fontFamily: SANS,
          fontSize: "0.92rem",
          fontWeight: 500,
          color: INK,
          letterSpacing: "-0.005em"
        }}
      >
        {page.name}
      </Typography>
      <Typography
        sx={{
          fontFamily: SANS,
          fontSize: "0.78rem",
          color: INK_FAINT,
          lineHeight: 1.3
        }}
      >
        {page.hint}
      </Typography>
    </MenuItem>
  );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: scrolled
          ? "rgba(255, 255, 255, 0.78)"
          : "rgba(255, 255, 255, 0.95)",
        backdropFilter: "saturate(180%) blur(20px)",
        WebkitBackdropFilter: "saturate(180%) blur(20px)",
        borderBottom: scrolled
          ? `1px solid ${INK_HAIRLINE}`
          : "1px solid transparent",
        transition: "background-color 0.2s ease, border-color 0.2s ease",
        color: INK
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 60, md: 68 },
            display: "flex",
            justifyContent: "space-between",
            gap: 2
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
            {wordmark}

            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 0.5
              }}
            >
              <Button
                disableRipple
                onClick={e => setPracticeAnchor(e.currentTarget)}
                endIcon={
                  <KeyboardArrowDownRoundedIcon
                    sx={{
                      fontSize: 18,
                      transition: "transform 0.18s ease",
                      transform: practiceAnchor
                        ? "rotate(180deg)"
                        : "rotate(0deg)"
                    }}
                  />
                }
                sx={navLinkSx(isPracticeActive)}
              >
                Practice
              </Button>
              <Menu
                anchorEl={practiceAnchor}
                open={Boolean(practiceAnchor)}
                onClose={closeAll}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{ paper: { sx: menuPaperSx } }}
              >
                {practicePages.map(renderPracticeItem)}
              </Menu>
              <Button
                disableRipple
                onClick={goHowItWorks}
                sx={navLinkSx(false)}
              >
                How it works
              </Button>
              <Button
                disableRipple
                onClick={goPricing}
                sx={navLinkSx(false)}
              >
                Pricing
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{ display: { xs: "flex", md: "none" }, alignItems: "center" }}
            >
              <IconButton
                onClick={e => setMobileAnchor(e.currentTarget)}
                sx={{
                  color: INK,
                  borderRadius: "10px",
                  "&:hover": { backgroundColor: "rgba(15, 17, 33, 0.04)" }
                }}
              >
                <MenuRoundedIcon />
              </IconButton>
              <Menu
                anchorEl={mobileAnchor}
                open={Boolean(mobileAnchor)}
                onClose={closeAll}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{ paper: { sx: { ...menuPaperSx, minWidth: 260 } } }}
              >
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: "0.66rem",
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: INK_FAINT,
                    px: 1.5,
                    pt: 1,
                    pb: 0.5
                  }}
                >
                  Practice
                </Typography>
                {practicePages.map(renderPracticeItem)}
                <Box
                  sx={{
                    borderTop: `1px solid ${INK_HAIRLINE}`,
                    mt: 0.5,
                    pt: 0.5
                  }}
                >
                  <MenuItem
                    onClick={goHowItWorks}
                    sx={{
                      py: 1.1,
                      px: 1.5,
                      borderRadius: "10px",
                      fontFamily: SANS,
                      fontSize: "0.92rem",
                      fontWeight: 500,
                      color: INK,
                      "&:hover": {
                        backgroundColor: "rgba(15, 17, 33, 0.04)"
                      }
                    }}
                  >
                    How it works
                  </MenuItem>
                  <MenuItem
                    onClick={goPricing}
                    sx={{
                      py: 1.1,
                      px: 1.5,
                      borderRadius: "10px",
                      fontFamily: SANS,
                      fontSize: "0.92rem",
                      fontWeight: 500,
                      color: INK,
                      "&:hover": {
                        backgroundColor: "rgba(15, 17, 33, 0.04)"
                      }
                    }}
                  >
                    Pricing
                  </MenuItem>
                </Box>
              </Menu>
            </Box>

            {user ? (
              <Box
                component={Link}
                to="/profile"
                sx={{
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 0.75,
                  py: 0.75,
                  borderRadius: "999px",
                  border: `1px solid ${
                    isProfileActive ? INK : INK_HAIRLINE
                  }`,
                  transition:
                    "border-color 0.18s ease, transform 0.15s ease, box-shadow 0.18s ease",
                  "&:hover": {
                    borderColor: "rgba(15, 17, 33, 0.32)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 8px 18px -14px rgba(15, 17, 33, 0.4)"
                  }
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    backgroundColor: INK,
                    color: "#fafaf7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: MONO,
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    letterSpacing: "0.04em"
                  }}
                >
                  {initials || "·"}
                </Box>
                <Typography
                  sx={{
                    display: { xs: "none", sm: "block" },
                    fontFamily: SANS,
                    fontSize: "0.86rem",
                    fontWeight: 500,
                    color: INK,
                    pr: 1
                  }}
                >
                  {userName ? userName.split(/\s+/)[0] : "Account"}
                </Typography>
              </Box>
            ) : (
              <Button
                component={Link}
                to="/login"
                disableRipple
                sx={{
                  fontFamily: SANS,
                  fontSize: "0.88rem",
                  fontWeight: 600,
                  textTransform: "none",
                  px: 2,
                  py: 0.9,
                  borderRadius: "10px",
                  color: "#fafaf7",
                  backgroundColor: INK,
                  boxShadow:
                    "inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 6px 14px -10px rgba(15, 17, 33, 0.5)",
                  transition: "transform 0.15s ease, background-color 0.18s ease",
                  "&:hover": {
                    backgroundColor: "#1a1d36",
                    transform: "translateY(-1px)"
                  },
                  "&:active": { transform: "translateY(0) scale(0.985)" }
                }}
              >
                Sign in
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default MenuBar;
